import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DollarSign, ChevronUp, ChevronDown, MapPin, AlertCircle } from 'lucide-react';
import { fairManagerApi } from '../services/api';
import { extractApiError } from '../utils/errorUtils';
import { confirmAsync } from '../utils/toast';
import { ROUTES } from '../constants/routes';
import { parseDateOnly } from '../utils/fairStatus'; // Issue #1672

/**
 * Fair Manager Fair Creation/Edit Form
 *
 * @component
 * @description Form for creating and editing fairs in the Fair Manager interface.
 * Supports multi-day scheduling, amenities, operational details, vendor requirements, and policies.
 *
 * ## E2E Testing
 *
 * ### Required Fields (form won't submit without these):
 * - `name` (`input[name="name"]` or `#name`) - Fair name, max 255 chars
 * - `city` (`#city`) - City name, required
 * - `state` (`#state`) - State abbreviation, max 2 chars, auto-uppercase
 * - `zip` (`#zip`) - ZIP code, max 10 chars
 * - `image` (`#image`) - Primary image URL (must be valid URL)
 * - `start_date` (`input[name="start_date"]` or `#start_date`) - Event start date (must be future date)
 * - `end_date` (`input[name="end_date"]` or `#end_date`) - Event end date (must be >= start_date)
 * - `total_slots` (`#total_slots`) - Total vendor booth spaces (min 0)
 *
 * ### Optional Fields:
 * - `email` (`#email`) - Contact email
 * - `description` (`#description`) - Fair description, max 2000 chars
 * - `phone` (`#phone`) - Phone number
 * - `website` (`#website`) - Website URL
 * - `street` (`#street`) - Street address
 * - `lat` (`#lat`) - Latitude coordinate
 * - `lng` (`#lng`) - Longitude coordinate
 * - Social media: `#facebook`, `#instagram`
 * - Amenities: `#has_live_music`, `#has_alcohol`, `#has_guest_appearances`, `#has_giveaways`, `#has_food_trucks`, `#is_affiliated`
 * - Operational: `#daily_hours`, `#load_in_time`, `#load_out_time`, `#parking_info`, `#weather_policy`, `#is_indoor`
 * - Attendee: `#attendee_entry_fee`, `#expected_attendance`, `[id="category-{category}"]` (attribute selector for spaces)
 * - Vendor: `#insurance_required`, `#application_deadline`, `#booth_includes`, `#power_availability`, `#offer_duration_hours` (Issue #640, #706, #848)
 * - Policies: `#prohibited_items`, `#sales_tax_info`, `#photography_policy`
 * - Scheduling: `#allows_per_day_reservation`
 *
 * ### Validation Rules:
 * - Fair name: Required, max 255 chars
 * - City, State, ZIP: Required
 * - Image URL: Required, must be valid URL
 * - Start/End dates: Required, end must be >= start
 * - Total slots: Required, min 0
 * - Application deadline: Must be before start_date if provided
 * - Character limits enforced on description (2000), parking_info (500), booth_includes (500), etc.
 *
 * @example
 * // E2E Test Example - Minimal Valid Fair
 * await page.fill('input[name="name"]', 'Summer Art Fair 2026');
 * await page.fill('#city', 'San Francisco');
 * await page.fill('#state', 'CA');
 * await page.fill('#zip', '94102');
 * await page.fill('#image', 'https://example.com/fair.jpg');
 * await page.fill('input[name="start_date"]', '2026-07-01');
 * await page.fill('input[name="end_date"]', '2026-07-03');
 * await page.fill('#total_slots', '25');
 * await page.click('button[type="submit"]');
 *
 * @see {@link ../../../tests/e2e/docs/FORM_FIELDS_REFERENCE.md} - Complete field documentation
 * @see {@link Issue #990} - Form validation rules documentation
 * @see {@link Issue #912} - Phase 2 edit mode functionality
 * @see {@link Issue #1172} - Unsaved changes warning (browser + React Router navigation)
 * @see {@link Issue #11} - Multi-day scheduling (CRITICAL)
 * @see {@link Issue #54} - Amenities & Features
 * @see {@link Issue #150} - Operational details, attendee info, vendor requirements, policies
 * @see {@link Issue #151} - Payment flow integration
 */
export default function FairManagerFairForm() {
  const navigate = useNavigate();
  const { fairId } = useParams<{ fairId: string }>();
  const isEditMode = Boolean(fairId);

  const [loading, setLoading] = useState(false);
  const [loadingFair, setLoadingFair] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [policiesExpanded, setPoliciesExpanded] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Default form data structure
  const getDefaultFormData = () => ({
    name: '',
    description: '',
    image: '',
    email: '',
    phone: '',
    website: '',
    // Multi-day scheduling fields (Issue #11) - CRITICAL
    start_date: '',
    end_date: '',
    total_slots: 0,
    allows_per_day_reservation: false,
    address: {
      street: '',
      city: '',
      state: '',
      zip: '',
      coordinates: {
        lat: null as number | null,
        lng: null as number | null
      }
    },
    social: {
      facebook: '',
      instagram: ''
    },
    // Amenities & Features (Issue #54)
    has_live_music: false,
    has_alcohol: false,
    has_guest_appearances: false,
    has_giveaways: false,
    has_food_trucks: false,
    is_affiliated: false,
    // Operational Details (Issue #150)
    daily_hours: '',
    load_in_time: '',
    load_out_time: '',
    parking_info: '',
    is_indoor: false,
    weather_policy: '',
    // Attendee Information (Issue #150)
    attendee_entry_fee: '', // Issue #890: Changed to string for Decimal precision
    expected_attendance: 0,
    fair_categories: [] as string[],
    // Vendor Requirements (Issue #150)
    insurance_required: false,
    application_deadline: '',
    booth_includes: '',
    power_availability: '',
    offer_duration_hours: 48, // Waitlist slot offer duration (Issue #640)
    // Policies (Issue #150)
    prohibited_items: '',
    sales_tax_info: '',
    photography_policy: ''
  });

  const [formData, setFormData] = useState(getDefaultFormData());
  const [initialFormData, setInitialFormData] = useState(getDefaultFormData());

  // Calculate event duration (Issue #1672: local-time parsing)
  const calculateDuration = () => {
    const start = parseDateOnly(formData.start_date);
    const end = parseDateOnly(formData.end_date);
    if (!start || !end) return 0;
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  // Validate application deadline (Issue #1672: local-time parsing)
  const validateApplicationDeadline = (): string | null => {
    const deadline = parseDateOnly(formData.application_deadline);
    const start = parseDateOnly(formData.start_date);
    if (!deadline || !start) return null;

    if (deadline >= start) {
      return 'Application deadline must be before the event start date';
    }
    return null;
  };

  // Load existing fair data when in edit mode (Issue #912 - Phase 2)
  useEffect(() => {
    if (!isEditMode || !fairId) return;

    const loadFairData = async () => {
      setLoadingFair(true);
      setError(null);
      try {
        const fair = await fairManagerApi.getFairById(Number(fairId));

        // Pre-populate form with existing fair data
        const loadedData = {
          name: fair.name || '',
          description: fair.description || '',
          image: fair.image || '',
          email: fair.email || '',
          phone: fair.phone || '',
          website: fair.website || '',
          start_date: fair.start_date || '',
          end_date: fair.end_date || '',
          total_slots: fair.total_slots || 0,
          allows_per_day_reservation: fair.allows_per_day_reservation || false,
          address: {
            street: fair.address?.street || '',
            city: fair.address?.city || '',
            state: fair.address?.state || '',
            zip: fair.address?.zip || '',
            coordinates: {
              lat: fair.address?.coordinates?.lat || null,
              lng: fair.address?.coordinates?.lng || null
            }
          },
          social: {
            facebook: fair.social?.facebook || '',
            instagram: fair.social?.instagram || ''
          },
          has_live_music: fair.has_live_music || false,
          has_alcohol: fair.has_alcohol || false,
          has_guest_appearances: fair.has_guest_appearances || false,
          has_giveaways: fair.has_giveaways || false,
          has_food_trucks: fair.has_food_trucks || false,
          is_affiliated: fair.is_affiliated || false,
          daily_hours: fair.daily_hours || '',
          load_in_time: fair.load_in_time || '',
          load_out_time: fair.load_out_time || '',
          parking_info: fair.parking_info || '',
          is_indoor: fair.is_indoor || false,
          weather_policy: fair.weather_policy || '',
          attendee_entry_fee: fair.attendee_entry_fee || '', // Issue #890: String for Decimal precision
          expected_attendance: fair.expected_attendance || 0,
          fair_categories: fair.fair_categories || [],
          insurance_required: fair.insurance_required || false,
          application_deadline: fair.application_deadline || '',
          booth_includes: fair.booth_includes || '',
          power_availability: fair.power_availability || '',
          offer_duration_hours: fair.offer_duration_hours || 48,
          prohibited_items: fair.prohibited_items || '',
          sales_tax_info: fair.sales_tax_info || '',
          photography_policy: fair.photography_policy || ''
        };

        setFormData(loadedData);
        setInitialFormData(loadedData);
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to load fair:', apiError);
        setError(apiError.message);
      } finally {
        setLoadingFair(false);
      }
    };

    loadFairData();
  }, [isEditMode, fairId]);

  // Track form changes - compare current form data with initial data (Issue #1172)
  // Uses JSON.stringify for deep comparison - efficient for forms with ~50 fields
  // For forms with 100+ fields, consider using a deep equality library
  useEffect(() => {
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialFormData);
    setHasUnsavedChanges(hasChanges);
  }, [formData, initialFormData]);

  // Browser navigation warning (beforeunload) - Issue #1172
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ''; // Required for Chrome
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // NOTE: useBlocker hook removed (Issue #1284) - incompatible with <BrowserRouter>
  // Navigation warnings handled by:
  // 1. beforeunload listener above (browser refresh/close)
  // 2. handleCancel function below (Cancel button click)

  // Handle category checkbox change
  const handleCategoryChange = (category: string) => {
    setFormData(prev => ({
      ...prev,
      fair_categories: prev.fair_categories.includes(category)
        ? prev.fair_categories.filter(c => c !== category)
        : [...prev.fair_categories, category]
    }));
  };

  // Handle cancel button - check for unsaved changes (Issue #1172)
  const handleCancel = async () => {
    if (hasUnsavedChanges) {
      const confirmed = await confirmAsync(
        'You have unsaved changes. Are you sure you want to leave?',
        {
          description: 'Your changes will be lost if you leave without saving.',
          confirmLabel: 'Discard Changes',
          cancelLabel: 'Keep Editing'
        }
      );

      if (confirmed) {
        navigate(ROUTES.FAIR_MANAGER.FAIRS);
      }
    } else {
      navigate(ROUTES.FAIR_MANAGER.FAIRS);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validation
    if (!formData.name.trim()) {
      setError('Fair name is required');
      setLoading(false);
      return;
    }

    if (!formData.start_date) {
      setError('Event start date is required');
      setLoading(false);
      return;
    }

    if (!formData.end_date) {
      setError('Event end date is required');
      setLoading(false);
      return;
    }

    if (new Date(formData.end_date) < new Date(formData.start_date)) {
      setError('Event end date must be on or after start date');
      setLoading(false);
      return;
    }

    if (formData.total_slots < 0) {
      setError('Total vendor booth spaces must be 0 or greater');
      setLoading(false);
      return;
    }

    if (!formData.address.city.trim()) {
      setError('City is required');
      setLoading(false);
      return;
    }

    if (!formData.address.state.trim()) {
      setError('State is required');
      setLoading(false);
      return;
    }

    if (!formData.address.zip.trim()) {
      setError('ZIP code is required');
      setLoading(false);
      return;
    }

    if (!formData.image.trim()) {
      setError('Primary fair image URL is required');
      setLoading(false);
      return;
    }

    const deadlineWarning = validateApplicationDeadline();
    if (deadlineWarning) {
      setError(deadlineWarning);
      setLoading(false);
      return;
    }

    try {
      // Create fair via Fair Manager API
      // Add required empty arrays for products and categories (legacy fields)
      // Ensure numeric fields are numbers, not strings (Issue #848)
      const fairData = {
        ...formData,
        offer_duration_hours: Number(formData.offer_duration_hours) || 48,
        total_slots: Number(formData.total_slots) || 0,
        attendee_entry_fee: Number(formData.attendee_entry_fee) || 0,
        expected_attendance: Number(formData.expected_attendance) || 0,
        products: [],
        categories: []
      };

      // Clear unsaved changes flag before navigation
      setHasUnsavedChanges(false);

      if (isEditMode && fairId) {
        // Update existing fair (Issue #912 - Phase 2)
        const updatedFair = await fairManagerApi.updateFair(Number(fairId), fairData);

        navigate(ROUTES.FAIR_MANAGER.FAIRS, {
          state: {
            message: 'Fair updated successfully!',
            fairId: updatedFair.id
          }
        });
      } else {
        // Create new fair
        const createdFair = await fairManagerApi.createFair(fairData);

        // Redirect to fair manager dashboard
        // Note: Payment flow (Issue #151) is handled automatically by backend
        // - If fee > $0: User receives email with Stripe payment link
        // - If fee = $0: Fair is auto-published
        navigate(ROUTES.FAIR_MANAGER.FAIRS, {
          state: {
            message: 'Fair created successfully! Check your email for payment instructions if a submission fee applies.',
            fairId: createdFair.id
          }
        });
      }
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} fair:`, apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const deadlineWarning = validateApplicationDeadline();

  // Show loading spinner while fetching fair data in edit mode
  if (loadingFair) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center" data-testid="fair-form-loading">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
            <p className="text-gray-600">Loading fair details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditMode ? 'Edit Fair Details' : 'Create New Fair'}
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          {isEditMode
            ? 'Update your fair details below. All fields marked with * are required.'
            : 'Fill out this comprehensive form to create your fair. All fields marked with * are required.'}
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4" data-testid="fair-form-error">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700" data-testid="fair-form-error-message">{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8" data-testid="fair-form">
        {/* Section 1: Basic Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Fair Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  maxLength={255}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="fair-name-input"
                />
                <p className="mt-1 text-xs text-gray-500">{formData.name.length}/255 characters</p>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Email
                </label>
                <input
                  id="email"
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  data-testid="fair-email-input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                id="description"
                rows={4}
                maxLength={2000}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                data-testid="fair-description-input"
              />
              <p className="mt-1 text-xs text-gray-500">{formData.description.length}/2000 characters - Describe your fair and what vendors/attendees can expect</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  id="phone"
                  type="tel"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                />
              </div>

              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                  Website URL
                </label>
                <input
                  id="website"
                  type="url"
                  placeholder="https://example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.website}
                  onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-2">
                Primary Fair Image URL <span className="text-red-500">*</span>
              </label>
              <input
                id="image"
                type="url"
                required
                placeholder="https://example.com/image.jpg"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.image}
                onChange={(e) => setFormData(prev => ({ ...prev, image: e.target.value }))}
                data-testid="fair-image-input"
              />
              <p className="mt-1 text-xs text-gray-500">Enter a direct URL to your main fair image</p>
            </div>
          </div>
        </div>

        {/* Section 2: Event Schedule (CRITICAL - Issue #11) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Event Schedule</h2>
          <p className="text-sm text-gray-600 mb-4">
            These fields are critical for the reservation system to function properly.
          </p>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-2">
                  Event Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  id="start_date"
                  name="start_date"
                  type="date"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.start_date}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, start_date: e.target.value }));
                    // Auto-adjust end_date if it becomes before start_date
                    if (formData.end_date && e.target.value > formData.end_date) {
                      setFormData(prev => ({ ...prev, end_date: e.target.value }));
                    }
                  }}
                  data-testid="fair-start-date-input"
                />
              </div>

              <div>
                <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 mb-2">
                  Event End Date <span className="text-red-500">*</span>
                </label>
                <input
                  id="end_date"
                  name="end_date"
                  type="date"
                  required
                  min={formData.start_date}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  data-testid="fair-end-date-input"
                />
              </div>
            </div>

            {formData.start_date && formData.end_date && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                <p className="text-sm text-primary-dark">
                  <strong>Event Duration:</strong> {calculateDuration()} {calculateDuration() === 1 ? 'day' : 'days'}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="total_slots" className="block text-sm font-medium text-gray-700 mb-2">
                  Total Vendor Booth Spaces <span className="text-red-500">*</span>
                </label>
                <input
                  id="total_slots"
                  type="number"
                  min="0"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.total_slots}
                  onChange={(e) => setFormData(prev => ({ ...prev, total_slots: parseInt(e.target.value) || 0 }))}
                  data-testid="fair-total-slots-input"
                />
                <p className="mt-1 text-xs text-gray-500">Number of vendor booths available for reservation</p>
              </div>

              <div className="flex items-center">
                <input
                  id="allows_per_day_reservation"
                  type="checkbox"
                  className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                  checked={formData.allows_per_day_reservation}
                  onChange={(e) => setFormData(prev => ({ ...prev, allows_per_day_reservation: e.target.checked }))}
                  data-testid="fair-per-day-reservation-checkbox"
                />
                <label htmlFor="allows_per_day_reservation" className="ml-2 block text-sm text-gray-700">
                  Allow Per-Day Reservations
                  <span className="block text-xs text-gray-500 mt-1">
                    Enable vendors to book individual days instead of full duration
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: Address */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <MapPin className="h-5 w-5 text-gray-600" />
            <span>Location</span>
          </h2>

          <div className="space-y-6">
            <div>
              <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-2">
                Street Address
              </label>
              <input
                id="street"
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.address.street}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  address: { ...prev.address, street: e.target.value }
                }))}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  id="city"
                  type="text"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.address.city}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    address: { ...prev.address, city: e.target.value }
                  }))}
                  data-testid="fair-city-input"
                />
              </div>

              <div>
                <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                  State <span className="text-red-500">*</span>
                </label>
                <input
                  id="state"
                  type="text"
                  required
                  maxLength={2}
                  placeholder="CA"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.address.state}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    address: { ...prev.address, state: e.target.value.toUpperCase() }
                  }))}
                  data-testid="fair-state-input"
                />
              </div>

              <div>
                <label htmlFor="zip" className="block text-sm font-medium text-gray-700 mb-2">
                  ZIP Code <span className="text-red-500">*</span>
                </label>
                <input
                  id="zip"
                  type="text"
                  required
                  maxLength={10}
                  placeholder="12345"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.address.zip}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    address: { ...prev.address, zip: e.target.value }
                  }))}
                  data-testid="fair-zip-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="lat" className="block text-sm font-medium text-gray-700 mb-2">
                  Latitude (Optional)
                </label>
                <input
                  id="lat"
                  type="number"
                  step="0.000001"
                  placeholder="37.7749"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.address.coordinates.lat ?? ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    address: {
                      ...prev.address,
                      coordinates: {
                        ...prev.address.coordinates,
                        lat: e.target.value ? parseFloat(e.target.value) : null
                      }
                    }
                  }))}
                />
              </div>

              <div>
                <label htmlFor="lng" className="block text-sm font-medium text-gray-700 mb-2">
                  Longitude (Optional)
                </label>
                <input
                  id="lng"
                  type="number"
                  step="0.000001"
                  placeholder="-122.4194"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.address.coordinates.lng ?? ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    address: {
                      ...prev.address,
                      coordinates: {
                        ...prev.address.coordinates,
                        lng: e.target.value ? parseFloat(e.target.value) : null
                      }
                    }
                  }))}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: Social Media */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Social Media</h2>

          <div className="space-y-6">
            <div>
              <label htmlFor="facebook" className="block text-sm font-medium text-gray-700 mb-2">
                Facebook Page URL
              </label>
              <input
                id="facebook"
                type="url"
                placeholder="https://facebook.com/yourpage"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.social.facebook}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  social: { ...prev.social, facebook: e.target.value }
                }))}
              />
            </div>

            <div>
              <label htmlFor="instagram" className="block text-sm font-medium text-gray-700 mb-2">
                Instagram Profile URL
              </label>
              <input
                id="instagram"
                type="url"
                placeholder="https://instagram.com/yourprofile"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.social.instagram}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  social: { ...prev.social, instagram: e.target.value }
                }))}
              />
            </div>
          </div>
        </div>

        {/* Section 5: Amenities & Features (Issue #54) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Amenities & Features</h2>
          <p className="text-sm text-gray-600 mb-4">
            Select the amenities and special features available at your fair.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="flex items-center">
              <input
                id="has_live_music"
                type="checkbox"
                className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                checked={formData.has_live_music}
                onChange={(e) => setFormData(prev => ({ ...prev, has_live_music: e.target.checked }))}
              />
              <label htmlFor="has_live_music" className="ml-2 block text-sm text-gray-700">
                🎵 Live Music
              </label>
            </div>

            <div className="flex items-center">
              <input
                id="has_alcohol"
                type="checkbox"
                className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                checked={formData.has_alcohol}
                onChange={(e) => setFormData(prev => ({ ...prev, has_alcohol: e.target.checked }))}
              />
              <label htmlFor="has_alcohol" className="ml-2 block text-sm text-gray-700">
                🍺 Beer/Wine/Alcohol
              </label>
            </div>

            <div className="flex items-center">
              <input
                id="has_guest_appearances"
                type="checkbox"
                className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                checked={formData.has_guest_appearances}
                onChange={(e) => setFormData(prev => ({ ...prev, has_guest_appearances: e.target.checked }))}
              />
              <label htmlFor="has_guest_appearances" className="ml-2 block text-sm text-gray-700">
                ⭐ Guest Appearances
              </label>
            </div>

            <div className="flex items-center">
              <input
                id="has_giveaways"
                type="checkbox"
                className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                checked={formData.has_giveaways}
                onChange={(e) => setFormData(prev => ({ ...prev, has_giveaways: e.target.checked }))}
              />
              <label htmlFor="has_giveaways" className="ml-2 block text-sm text-gray-700">
                🎁 Giveaways/Raffles
              </label>
            </div>

            <div className="flex items-center">
              <input
                id="has_food_trucks"
                type="checkbox"
                className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                checked={formData.has_food_trucks}
                onChange={(e) => setFormData(prev => ({ ...prev, has_food_trucks: e.target.checked }))}
              />
              <label htmlFor="has_food_trucks" className="ml-2 block text-sm text-gray-700">
                🚚 Food Trucks
              </label>
            </div>

            <div className="flex items-center">
              <input
                id="is_affiliated"
                type="checkbox"
                className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                checked={formData.is_affiliated}
                onChange={(e) => setFormData(prev => ({ ...prev, is_affiliated: e.target.checked }))}
              />
              <label htmlFor="is_affiliated" className="ml-2 block text-sm text-gray-700">
                🤝 Affiliated Organization
              </label>
            </div>
          </div>
        </div>

        {/* Section 6: Operational Details (Issue #150) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Operational Details</h2>
          <p className="text-sm text-gray-600 mb-4">
            Provide operational information for vendors and attendees.
          </p>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="daily_hours" className="block text-sm font-medium text-gray-700 mb-2">
                  Daily Operating Hours
                </label>
                <input
                  id="daily_hours"
                  type="text"
                  placeholder="e.g., 9:00 AM - 5:00 PM"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.daily_hours}
                  onChange={(e) => setFormData(prev => ({ ...prev, daily_hours: e.target.value }))}
                />
              </div>

              <div className="flex items-center">
                <input
                  id="is_indoor"
                  type="checkbox"
                  className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                  checked={formData.is_indoor}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_indoor: e.target.checked }))}
                />
                <label htmlFor="is_indoor" className="ml-2 block text-sm text-gray-700">
                  Indoor Event
                  <span className="block text-xs text-gray-500">Check if event is held indoors</span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="load_in_time" className="block text-sm font-medium text-gray-700 mb-2">
                  Vendor Load-In Time
                </label>
                <input
                  id="load_in_time"
                  type="time"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.load_in_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, load_in_time: e.target.value }))}
                />
                <p className="mt-1 text-xs text-gray-500">When vendors can arrive to set up</p>
              </div>

              <div>
                <label htmlFor="load_out_time" className="block text-sm font-medium text-gray-700 mb-2">
                  Vendor Load-Out Time
                </label>
                <input
                  id="load_out_time"
                  type="time"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.load_out_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, load_out_time: e.target.value }))}
                />
                <p className="mt-1 text-xs text-gray-500">When vendors can start packing up</p>
              </div>
            </div>

            <div>
              <label htmlFor="parking_info" className="block text-sm font-medium text-gray-700 mb-2">
                Parking Information
              </label>
              <textarea
                id="parking_info"
                rows={3}
                maxLength={500}
                placeholder="Describe parking options for vendors and attendees"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.parking_info}
                onChange={(e) => setFormData(prev => ({ ...prev, parking_info: e.target.value }))}
              />
              <p className="mt-1 text-xs text-gray-500">{formData.parking_info.length}/500 characters</p>
            </div>

            <div>
              <label htmlFor="weather_policy" className="block text-sm font-medium text-gray-700 mb-2">
                Weather/Cancellation Policy
              </label>
              <textarea
                id="weather_policy"
                rows={4}
                maxLength={1000}
                placeholder="Describe your policy for inclement weather, cancellations, and refunds"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.weather_policy}
                onChange={(e) => setFormData(prev => ({ ...prev, weather_policy: e.target.value }))}
              />
              <p className="mt-1 text-xs text-gray-500">{formData.weather_policy.length}/1000 characters</p>
            </div>
          </div>
        </div>

        {/* Section 7: Attendee Information (Issue #150) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Attendee Information</h2>
          <p className="text-sm text-gray-600 mb-4">
            Information about attendee entry and expected attendance.
          </p>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="attendee_entry_fee" className="block text-sm font-medium text-gray-700 mb-2">
                  Attendee Entry Fee
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="attendee_entry_fee"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    value={formData.attendee_entry_fee}
                    onChange={(e) => setFormData(prev => ({ ...prev, attendee_entry_fee: e.target.value }))}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Entry fee charged to attendees ($0.00 = free)</p>
              </div>

              <div>
                <label htmlFor="expected_attendance" className="block text-sm font-medium text-gray-700 mb-2">
                  Expected Attendance
                </label>
                <input
                  id="expected_attendance"
                  type="number"
                  min="0"
                  placeholder="500"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.expected_attendance}
                  onChange={(e) => setFormData(prev => ({ ...prev, expected_attendance: parseInt(e.target.value) || 0 }))}
                />
                <p className="mt-1 text-xs text-gray-500">Estimated number of visitors</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fair Categories
              </label>
              <div className="space-y-2">
                {['craft fair', 'farmers market', 'art show', 'holiday market', 'music festival'].map(category => (
                  <div key={category} className="flex items-center">
                    <input
                      id={`category-${category}`}
                      type="checkbox"
                      className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                      checked={formData.fair_categories.includes(category)}
                      onChange={() => handleCategoryChange(category)}
                    />
                    <label htmlFor={`category-${category}`} className="ml-2 block text-sm text-gray-700 capitalize">
                      {category}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Section 8: Vendor Requirements (Issue #150) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Vendor Requirements</h2>
          <p className="text-sm text-gray-600 mb-4">
            Specify requirements and information for vendors.
          </p>

          <div className="space-y-6">
            <div className="flex items-center">
              <input
                id="insurance_required"
                type="checkbox"
                className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                checked={formData.insurance_required}
                onChange={(e) => setFormData(prev => ({ ...prev, insurance_required: e.target.checked }))}
              />
              <label htmlFor="insurance_required" className="ml-2 block text-sm text-gray-700">
                Liability Insurance Required
                <span className="block text-xs text-gray-500">Vendors must provide proof of insurance</span>
              </label>
            </div>

            {/* Waitlist Slot Offer Duration (Issue #640, #706, #848) */}
            <div>
              <label htmlFor="offer_duration_hours" className="block text-sm font-medium text-gray-700 mb-2">
                Waitlist Slot Offer Duration (hours) *
              </label>
              <input
                id="offer_duration_hours"
                name="offer_duration_hours"
                type="number"
                min="1"
                max="168"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.offer_duration_hours}
                onChange={(e) => setFormData(prev => ({ ...prev, offer_duration_hours: parseInt(e.target.value) || 48 }))}
              />
              <p className="mt-1 text-xs text-gray-500">
                How long vendors have to accept waitlist slot offers (1-168 hours). Default: 48 hours (2 days).
              </p>
            </div>

            <div>
              <label htmlFor="application_deadline" className="block text-sm font-medium text-gray-700 mb-2">
                Vendor Application Deadline
              </label>
              <input
                id="application_deadline"
                type="date"
                max={formData.start_date}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.application_deadline}
                onChange={(e) => setFormData(prev => ({ ...prev, application_deadline: e.target.value }))}
                data-testid="fair-application-deadline-input"
              />
              <p className="mt-1 text-xs text-gray-500">Last day vendors can apply (must be before event start date)</p>
              {deadlineWarning && (
                <p className="mt-1 text-xs text-red-600 flex items-center space-x-1">
                  <AlertCircle className="h-3 w-3" />
                  <span>{deadlineWarning}</span>
                </p>
              )}
            </div>

            <div>
              <label htmlFor="booth_includes" className="block text-sm font-medium text-gray-700 mb-2">
                What's Included in Booth Rental
              </label>
              <textarea
                id="booth_includes"
                rows={3}
                maxLength={500}
                placeholder="e.g., Table, 2 chairs, 10x10 tent, electricity access"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.booth_includes}
                onChange={(e) => setFormData(prev => ({ ...prev, booth_includes: e.target.value }))}
              />
              <p className="mt-1 text-xs text-gray-500">{formData.booth_includes.length}/500 characters</p>
            </div>

            <div>
              <label htmlFor="power_availability" className="block text-sm font-medium text-gray-700 mb-2">
                Power/Electrical Availability
              </label>
              <textarea
                id="power_availability"
                rows={3}
                maxLength={500}
                placeholder="Describe electrical power options and any additional fees"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.power_availability}
                onChange={(e) => setFormData(prev => ({ ...prev, power_availability: e.target.value }))}
              />
              <p className="mt-1 text-xs text-gray-500">{formData.power_availability.length}/500 characters</p>
            </div>
          </div>
        </div>

        {/* Section 9: Policies (Collapsible - Issue #150) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <button
            type="button"
            onClick={() => setPoliciesExpanded(!policiesExpanded)}
            className="w-full flex items-center justify-between text-left"
          >
            <h2 className="text-xl font-semibold text-gray-900">Policies & Rules</h2>
            {policiesExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
          </button>

          {policiesExpanded && (
            <div className="mt-4 space-y-6">
              <div>
                <label htmlFor="prohibited_items" className="block text-sm font-medium text-gray-700 mb-2">
                  Prohibited Items
                </label>
                <textarea
                  id="prohibited_items"
                  rows={4}
                  maxLength={1000}
                  placeholder="List items that vendors cannot sell at your fair"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.prohibited_items}
                  onChange={(e) => setFormData(prev => ({ ...prev, prohibited_items: e.target.value }))}
                />
                <p className="mt-1 text-xs text-gray-500">{formData.prohibited_items.length}/1000 characters</p>
              </div>

              <div>
                <label htmlFor="sales_tax_info" className="block text-sm font-medium text-gray-700 mb-2">
                  Sales Tax Information
                </label>
                <textarea
                  id="sales_tax_info"
                  rows={4}
                  maxLength={1000}
                  placeholder="Describe sales tax requirements for vendors"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.sales_tax_info}
                  onChange={(e) => setFormData(prev => ({ ...prev, sales_tax_info: e.target.value }))}
                />
                <p className="mt-1 text-xs text-gray-500">{formData.sales_tax_info.length}/1000 characters</p>
              </div>

              <div>
                <label htmlFor="photography_policy" className="block text-sm font-medium text-gray-700 mb-2">
                  Photography & Media Policy
                </label>
                <textarea
                  id="photography_policy"
                  rows={4}
                  maxLength={1000}
                  placeholder="Describe your policy for photography and media at the event"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.photography_policy}
                  onChange={(e) => setFormData(prev => ({ ...prev, photography_policy: e.target.value }))}
                />
                <p className="mt-1 text-xs text-gray-500">{formData.photography_policy.length}/1000 characters</p>
              </div>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-between border-t border-gray-200 pt-6">
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            data-testid="fair-form-cancel-button"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={loading}
            className="px-8 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            data-testid="fair-form-submit-button"
          >
            {loading
              ? (isEditMode ? 'Updating Fair...' : 'Creating Fair...')
              : (isEditMode ? 'Update Fair' : 'Create Fair')}
          </button>
        </div>
      </form>
    </div>
  );
}
